import { makeAutoObservable, runInAction, reaction, computed } from 'mobx'
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from 'firebase/firestore'
import { getCollection } from '../../../shared/api/firestore'
import FirebaseApp from '../../../shared/api/firebase'
import {
  uploadClientAvatar as uploadClientAvatarApi,
  deleteImageByUrl,
} from '../../../shared/api/storage'

const db = getFirestore(FirebaseApp)

class ClientStore {
  clients = []
  isLoading = false
  searchInput = ''
  isUploading = false

  constructor(rootStore) {
    makeAutoObservable(
      this,
      { rootStore: false, sortedClients: computed, clientsById: computed },
      { autoBind: true },
    )
    this.rootStore = rootStore
    this.organizationStore = rootStore.organizationStore
    this.errorStore = rootStore.errorStore
    this.sortStore = rootStore.sortStore

    // Реакция для автоматической загрузки клиентов при изменении ID организации
    reaction(
      () => this.organizationStore.organization?.id,
      (orgId) => {
        if (orgId) {
          this.fetchClients(orgId)
        } else {
          // Очищаем клиентов, если организации нет
          runInAction(() => {
            this.clients = []
          })
        }
      },
      { fireImmediately: true }, // Запускаем сразу, чтобы поймать начальное состояние
    )
  }

  async fetchClients(organizationId) {
    if (!organizationId) return
    this.isLoading = true
    try {
      const clientsList = await getCollection([
        'organizations',
        organizationId,
        'clients',
      ])
      runInAction(() => {
        this.clients = clientsList
      })
    } catch (error) {
      console.error('Не удалось загрузить клиентов:', error)
      this.errorStore.showError('Не удалось загрузить список клиентов')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async addClient(clientData, setError = () => {}) {
    const orgId = this.organizationStore.organization?.id
    if (!orgId) {
      this.errorStore.showError('Организация не найдена.')
      return null
    }
    // Базовая валидация, чтобы не создавать пустых клиентов из Autocomplete
    if (!clientData.name?.trim()) {
      // Не показываем ошибку, так как это может быть просто очистка поля
      return null
    }

    this.isLoading = true
    try {
      const clientCollectionRef = collection(
        db,
        'organizations',
        orgId,
        'clients',
      )

      // Проверка на дубликат номера телефона, только если он предоставлен
      if (clientData.phone) {
        const q = query(
          clientCollectionRef,
          where('phone', '==', clientData.phone),
        )
        const querySnapshot = await getDocs(q)
        if (!querySnapshot.empty) {
          const errorMessage = 'Клиент с таким телефоном уже существует.'
          // Используем setError для формы и показываем общую ошибку
          setError('phone', { type: 'manual', message: errorMessage })
          this.errorStore.showError(errorMessage)
          return null
        }
      }

      const newDocRef = doc(clientCollectionRef) // Auto-generates ID

      const newClientData = {
        ...clientData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }

      await setDoc(newDocRef, newClientData)

      // Оптимистичное обновление
      runInAction(() => {
        const optimisticClient = {
          ...newClientData,
          id: newDocRef.id,
          createdAt: new Date(),
          updatedAt: new Date(),
        }
        this.clients = [...this.clients, optimisticClient]
        this.errorStore.showSuccess('Клиент успешно добавлен.')
      })
      return newDocRef.id
    } catch (error) {
      console.error('Ошибка при добавлении клиента:', error)
      this.errorStore.showError(
        'Не удалось добавить клиента. Проверьте соединение с интернетом.',
      )
      return null
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  // Приватный хелпер для инкапсуляции логики обновления данных клиента.
  // Обновляет документ в Firestore и локальное состояние в MobX.
  async _updateClientData(clientId, data) {
    const orgId = this.organizationStore.organization.id
    if (!orgId) {
      // Эта ошибка не должна показываться пользователю, это проблема целостности данных.
      throw new Error('ID организации отсутствует при обновлении клиента.')
    }

    const clientDocRef = doc(db, 'organizations', orgId, 'clients', clientId)
    await updateDoc(clientDocRef, {
      ...data,
      updatedAt: serverTimestamp(),
    })

    // Оптимистичное обновление локального состояния
    runInAction(() => {
      const index = this.clients.findIndex((c) => c.id === clientId)
      if (index !== -1) {
        this.clients = this.clients.map((client) =>
          client.id === clientId ? { ...client, ...data } : client,
        )
      }
    })
  }

  async updateClient(clientId, clientData, setError) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !clientId) return false

    this.isLoading = true
    try {
      // Проверка на дубликат номера телефона, исключая текущего клиента
      // Эта логика специфична для формы, поэтому оставляем ее здесь.
      if (clientData.phone && setError) {
        const clientCollectionRef = collection(
          db,
          'organizations',
          orgId,
          'clients',
        )
        const q = query(
          clientCollectionRef,
          where('phone', '==', clientData.phone),
        )
        const querySnapshot = await getDocs(q)
        const duplicate = querySnapshot.docs.find((doc) => doc.id !== clientId)
        if (duplicate) {
          setError('phone', {
            type: 'manual',
            message: 'Клиент с таким телефоном уже существует.',
          })
          return false // Указываем на неудачу
        }
      }

      await this._updateClientData(clientId, clientData)

      runInAction(() => {
        this.errorStore.showSuccess('Данные клиента успешно обновлены.')
      })
      return true
    } catch (error) {
      console.error('Ошибка при обновлении клиента:', error)
      this.errorStore.showError(
        'Не удалось обновить данные клиента. Проверьте соединение с интернетом.',
      )
      return false
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async removeClient(clientId) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !clientId) return

    this.isLoading = true
    try {
      const clientDocRef = doc(db, 'organizations', orgId, 'clients', clientId)
      const clientToRemove = this.clients.find((c) => c.id === clientId)
      await deleteDoc(clientDocRef)

      // Оптимистичное обновление
      runInAction(() => {
        this.clients = this.clients.filter((c) => c.id !== clientId)
        this.errorStore.showSuccess('Клиент удален.')
      })

      if (clientToRemove?.avatar) {
        await deleteImageByUrl(clientToRemove.avatar)
      }
    } catch (error) {
      console.error('Ошибка при удалении клиента:', error)
      this.errorStore.showError('Не удалось удалить клиента.')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async uploadClientAvatar(clientId, file) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !clientId || !file) return null

    this.isUploading = true
    try {
      const client = this.clients.find((c) => c.id === clientId)
      if (client?.avatar) {
        await deleteImageByUrl(client.avatar)
      }

      const avatarUrl = await uploadClientAvatarApi(orgId, clientId, file)

      await this._updateClientData(clientId, { avatar: avatarUrl })
      return avatarUrl
    } catch (error) {
      console.error('Ошибка при загрузке аватара клиента:', error)
      this.errorStore.showError('Не удалось загрузить аватар.')
      return null
    } finally {
      runInAction(() => (this.isUploading = false))
    }
  }

  async removeClientAvatar(clientId) {
    const client = this.clients.find((c) => c.id === clientId)
    if (!client?.avatar) return

    this.isUploading = true
    try {
      await deleteImageByUrl(client.avatar)
      await this._updateClientData(clientId, { avatar: null })
    } catch (error) {
      this.errorStore.showError('Не удалось удалить аватар.')
    } finally {
      runInAction(() => (this.isUploading = false))
    }
  }

  search(term) {
    this.searchInput = term
  }

  get sortedClients() {
    let filteredClients = [...this.clients]

    if (this.searchInput) {
      const lowercasedFilter = this.searchInput.toLowerCase()
      filteredClients = filteredClients.filter(
        (client) =>
          client.name?.toLowerCase().includes(lowercasedFilter) ||
          client.phone?.includes(this.searchInput),
      )
    }

    // Используем общий сортировщик для консистентности
    return filteredClients.sort(this.sortStore.sorter)
  }

  get clientsById() {
    return this.clients.reduce((acc, client) => {
      acc[client.id] = client
      return acc
    }, {})
  }
}

export default ClientStore
